import { Box, Card, styled, Button, TextField, MenuItem, Typography, CircularProgress, InputAdornment } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import FieldVisitTable from "page-sections/FieldVisit/FieldVisitTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { GetApp, ArrowForward, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FieldVisitExportModal from "page-sections/FieldVisit/FieldVisitExportModal";
// ─── CHANGE 1: Import buttonConfig ─────────────────────────────────────────────
import buttonConfig from "config/ConfigAccessButton";
// ─────────────────────────────────────────────────────────────────────────────

const FieldVisit = () => {
    const { setPageTitle } = usePageTitle();
    const navigate = useNavigate();
    const [fieldVisits, setFieldVisits] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [companyFilter, setCompanyFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [totalVisits, setTotalVisits] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];

    useEffect(() => {
        setPageTitle('Field Visit');
    }, [setPageTitle]);

    // ─── CHANGE 2: Setup permissions from config ───────────────────────────────
    const fieldVisitConfig = buttonConfig["Field visit"] || {};
    const can = (action) => !!fieldVisitConfig[action];
    // ─────────────────────────────────────────────────────────────────────────

    const isCompanyUser = apiService.isCompanyUser();
    const companyId = apiService.getCompanyId();

    const fetchCompanies = useCallback(async () => {
        if (isCompanyUser) {
            const details = apiService.getCompanyDetails();
            const id = apiService.getCompanyId();
            const singleCompany = {
                id: id,
                name: details?.name || "My Company",
            };
            setCompanyOptions([singleCompany]);
            setCompanyFilter(id?.toString() || "");
            return;
        }

        setLoadingCompanies(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_company_choices);
            const options = response?.data?.Company || response?.data?.choices || response?.data || [];
            if (Array.isArray(options)) {
                setCompanyOptions(options);
            } else {
                setCompanyOptions([]);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanyOptions([]);
        } finally {
            setLoadingCompanies(false);
        }
    }, [isCompanyUser]);

    const fetchCategories = useCallback(async (selectedCompanyId = null) => {
        setLoadingCategories(true);
        try {
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            
            if (!targetCompanyId) {
                setCategories([]);
                return;
            }

            const userId = localStorage.getItem('user_id');
            const response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${targetCompanyId}&user_id=${userId}`);
            
            if (response?.success) {
                const categoryList = response.data?.ProductCategory || [];
                setCategories(categoryList);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, [isCompanyUser, companyId]);

    const fetchFieldVisits = useCallback(async (selectedCompanyId = null, page = 1, pageSizeParam = 10, search = "") => {
        setLoading(true);
        try {
            let url = API_ENDPOINTS.GET_field_visit;
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('page_size', String(pageSizeParam));
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            if (targetCompanyId) params.set('company_id', targetCompanyId);
            
            if (search && search.trim()) {
                params.set('search', search.trim());
            }
            
            url += (url.includes('?') ? '&' : '?') + params.toString();

            const response = await apiService.get(url);

            if (response?.success && response?.data?.FieldVisit) {
                setFieldVisits(response.data.FieldVisit);
            } else {
                setFieldVisits([]);
            }
            const paginationData = response?.pagination || response?.data?.pagination || {};
            if (Object.keys(paginationData).length > 0) {
                setPagination({
                    current_page: paginationData.current_page ?? page,
                    page_size: paginationData.page_size ?? pageSizeParam,
                    total_items: paginationData.total_items ?? 0,
                    total_pages: paginationData.total_pages ?? 1,
                    has_next: paginationData.has_next ?? false,
                    has_previous: paginationData.has_previous ?? false,
                    next_page_number: paginationData.next_page_number ?? null,
                    previous_page_number: paginationData.previous_page_number ?? null,
                });
                setCurrentPage(paginationData.current_page ?? page);
                setPageSize(paginationData.page_size ?? pageSizeParam);
            } else {
                const list = response?.data?.FieldVisit || [];
                setPagination(prev => ({ ...prev, total_items: list.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
            }
        } catch (error) {
            console.error("API error:", error);
            setFieldVisits([]);
        } finally {
            setLoading(false);
        }
    }, [isCompanyUser, companyId]);

    const handleFieldVisitPageChange = (_, value) => {
        setCurrentPage(value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, value, pageSize, searchTerm);
    };

    const handleFieldVisitPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, newSize, searchTerm);
    };

    const handleSearch = () => {
        if (!searchValue.trim()) return;
        setSearchTerm(searchValue);
        setCurrentPage(1);
        setHasSearched(true);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, pageSize, searchValue);
    };

    const handleClearSearch = () => {
        setSearchValue("");
        setSearchTerm("");
        setCurrentPage(1);
        setHasSearched(false);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, pageSize, "");
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && searchValue.trim()) {
            handleSearch();
        }
    };

    useEffect(() => {
        if (searchValue === "" && searchTerm !== "") {
            handleClearSearch();
        }
    }, [searchValue]);

    const fetchDashboardData = useCallback(async () => {
        setLoadingDashboard(true);
        try {
            const today = new Date();
            const dateFrom = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
            const dateTo = new Date(today.getFullYear() + 1, 0, 31).toISOString().split('T')[0];
            
            const url = `${API_ENDPOINTS.GET_field_visit_dashboard}?user_type=agent`;
            const response = await apiService.get(url);
            
            if (response?.success && response?.data?.summary) {
                setTotalVisits(response.data.summary.total_visits);
            } else {
                setTotalVisits(null);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setTotalVisits(null);
        } finally {
            setLoadingDashboard(false);
        }
    }, []);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    useEffect(() => {
        if (companyFilter || (isCompanyUser && companyId)) {
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchCategories(targetCompanyId);
            setCategoryFilter("");
        } else {
            setCategories([]);
            setCategoryFilter("");
        }
    }, [companyFilter, isCompanyUser, companyId, fetchCategories]);

    useEffect(() => {
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
    }, [companyFilter, isCompanyUser, companyId, fetchFieldVisits]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const handleCompanyFilterChange = (e) => {
        setCompanyFilter(e.target.value);
        setSearchTerm("");
        setSearchValue("");
        setHasSearched(false);
    };

    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        if (targetCompanyId) {
            fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
        }
    };

    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    return (
        <Box pt={2} pb={4}>
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                    backgroundColor: "background.default",
                    pt: 0.5,
                    pb: 1,
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "12px",
                        p: "12px 16px",
                        mb: 2,
                        bgcolor: "background.paper",
                        borderRadius: "10px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        border: "1px solid #e5e7eb",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flex: { xs: "1 1 100%", lg: "1 1 0" },
                            minWidth: { xs: "100%", lg: 280 },
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by Date, farmer name, Contact, village etc"
                            value={searchValue}
                            onChange={(e) => {
                                setSearchValue(e.target.value);
                                if (hasSearched && e.target.value !== searchTerm) {
                                    setHasSearched(false);
                                }
                            }}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ fontSize: 20, color: "text.disabled" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                    height: 40,
                                    borderRadius: "8px",
                                    fontSize: 14,
                                    pl: 0.5,
                                    "& fieldset": { borderColor: "#d1d5db" },
                                    "&:hover fieldset": { borderColor: "#9ca3af" },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#d1d5db",
                                        boxShadow: "none",
                                    },
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<Search />}
                            onClick={handleSearch}
                            disabled={!searchValue.trim() || (searchValue === searchTerm && hasSearched)}
                            sx={{
                                height: 40,
                                minWidth: 90,
                                borderRadius: 2,
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                px: 2,
                                boxShadow: "none",
                                "&:hover": { boxShadow: "none" },
                            }}
                        >
                            Search
                        </Button>
                    </Box>

                    {!isCompanyUser && (
                        <TextField
                            select
                            size="small"
                            value={companyFilter}
                            onChange={handleCompanyFilterChange}
                            disabled={loading || loadingCompanies}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected) => {
                                    if (!selected) {
                                        return (
                                            <Typography component="span" sx={{ color: "text.secondary", fontSize: 14 }}>
                                                All Category
                                            </Typography>
                                        );
                                    }
                                    const company = companyOptions.find(
                                        (item) => String(item.id) === String(selected)
                                    );
                                    return company?.name || "All Category";
                                },
                            }}
                            sx={{
                                width: 160,
                                flexShrink: 0,
                                "& .MuiOutlinedInput-root": {
                                    height: 40,
                                    borderRadius: "8px",
                                    fontSize: 14,
                                    "& fieldset": { borderColor: "#d1d5db" },
                                },
                            }}
                        >
                            <MenuItem value="">All Category</MenuItem>
                            {loadingCompanies ? (
                                <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Loading companies...
                                </MenuItem>
                            ) : (
                                companyOptions.map((company) => (
                                    <MenuItem key={company.id} value={company.id}>
                                        {company.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    )}

                    {totalVisits !== null && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 40,
                                px: "14px",
                                py: "6px",
                                borderRadius: "20px",
                                bgcolor: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                color: "#166534",
                                fontSize: 13,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            {loadingDashboard ? (
                                <CircularProgress size={16} sx={{ color: "#166534" }} />
                            ) : (
                                `Total Field Visits: ${totalVisits.toLocaleString()}`
                            )}
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        startIcon={<GetApp />}
                        onClick={() => can('export_to_excel') && handleExportClick()}
                        disabled={!can('export_to_excel')}
                        sx={{
                            height: 40,
                            borderRadius: 2,
                            px: 2,
                            fontSize: 14,
                            fontWeight: 500,
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            opacity: !can('export_to_excel') ? 0.6 : 1,
                            boxShadow: "none",
                            "&:hover": { boxShadow: "none" },
                        }}
                    >
                        Export to Excel
                    </Button>
                </Box>
            </Box>

            <Card>
                <Box sx={{ mt: 1 }}>
                    <FieldVisitTable
                        fieldVisits={fieldVisits}
                        searchQuery={searchTerm}
                        loading={loading}
                        onRefresh={() => {
                            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                            fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
                            fetchDashboardData();
                        }}
                        pagination={pagination}
                        onPageChange={handleFieldVisitPageChange}
                        onPageSizeChange={handleFieldVisitPageSizeChange}
                        pageSizeOptions={pageSizeOptions}
                        // ─── CHANGE 4: Pass view_image permission to table ─────────────────────
                        canViewImage={can('view_image')}
                        // ───────────────────────────────────────────────────────────────────────
                    />
                </Box>
            </Card>

            <FieldVisitExportModal
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={() => {
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
                }}
            />
        </Box>
    );
};

export default FieldVisit;